import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import api, { num, money } from "@/lib/api";

const PERIODS = [["today", "Today"], ["week", "This Week"], ["month", "This Month"], ["lifetime", "Lifetime"]];

function Metric({ label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-4 text-center">
      <div className="font-mono-metric text-xl font-bold text-[#0A1128]">{value}</div>
      <div className="text-[11px] text-slate-400 uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  );
}

export default function WorkerPerformance() {
  const [perf, setPerf] = useState(null);
  const [period, setPeriod] = useState("today");
  const [trend, setTrend] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [badges, setBadges] = useState(null);

  useEffect(() => {
    api.get("/me/performance").then((r) => setPerf(r.data.performance)).catch(() => {});
    api.get("/me/performance/trend?days=7").then((r) => setTrend(r.data.items)).catch(() => {});
    api.get("/me/earnings").then((r) => setEarnings(r.data)).catch(() => {});
    api.get("/me/badges").then((r) => setBadges(r.data)).catch(() => {});
  }, []);

  const m = perf?.[period] || {};
  return (
    <div className="space-y-5">
      <h1 className="font-head text-2xl font-extrabold text-[#0A1128]">My Performance</h1>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {PERIODS.map(([k, label]) => (
          <button key={k} onClick={() => setPeriod(k)} data-testid={`perf-period-${k}`} className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap ${period === k ? "bg-[#1D4ED8] text-white" : "bg-white border border-slate-200 text-slate-500"}`}>{label}</button>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Metric label="Tasks Done" value={num(m.tasks_completed)} />
        <Metric label="Completion" value={`${m.completion_rate || 0}%`} />
        <Metric label="Leads" value={num(m.leads)} />
        <Metric label="Calls" value={num(m.calls)} />
        <Metric label="Visits" value={num(m.visits)} />
        <Metric label="Meetings" value={num(m.meetings)} />
        <Metric label="Sales" value={num(m.sales)} />
        <Metric label="Revenue" value={money(m.revenue)} />
        <Metric label="Earnings" value={money(m.earnings)} />
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-4">
        <h2 className="font-head font-bold text-[#0A1128] mb-3">7-day trend</h2>
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={trend}>
            <XAxis dataKey="date" stroke="#94A3B8" fontSize={11} />
            <YAxis stroke="#94A3B8" fontSize={11} />
            <Tooltip />
            <Line type="monotone" dataKey="tasks" stroke="#1D4ED8" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="leads" stroke="#06B6D4" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {badges && (
        <div className="bg-white rounded-2xl border border-slate-100 p-4" data-testid="worker-badges">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-head font-bold text-[#0A1128]">Achievements</h2>
            <span className="text-xs font-semibold text-[#1D4ED8]">{badges.earned_count} / {badges.total} earned</span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {badges.badges.map((b) => (
              <div key={b.key} data-testid={`badge-${b.key}`} className={`rounded-2xl p-3 text-center border ${b.earned ? "bg-gradient-to-b from-[#FEF3C7] to-white border-[#D4AF37]" : "bg-slate-50 border-slate-100 opacity-70"}`}>
                <div className={`text-2xl ${b.earned ? "" : "grayscale opacity-50"}`}>{b.icon}</div>
                <div className="text-[11px] font-semibold text-[#0A1128] mt-1 leading-tight">{b.title}</div>
                <div className="text-[9px] text-slate-400 mt-0.5">{b.desc}</div>
                {!b.earned && (
                  <div className="mt-1.5 h-1 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-[#D4AF37]" style={{ width: `${b.progress}%` }} /></div>
                )}
                {b.earned && <div className="text-[9px] font-bold text-[#B8860B] mt-1">UNLOCKED</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {earnings && (
        <div className="bg-white rounded-2xl border border-slate-100 p-4">
          <h2 className="font-head font-bold text-[#0A1128] mb-3">Earnings breakdown</h2>
          <div className="space-y-2">
            {Object.entries(earnings.by_status || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm"><span className="capitalize text-slate-500">{k}</span><span className="font-mono-metric font-semibold text-[#0A1128]">{money(v)}</span></div>
            ))}
            <div className="flex justify-between text-sm border-t border-slate-100 pt-2 mt-2"><span className="font-semibold text-[#0A1128]">Total</span><span className="font-mono-metric font-bold text-[#1D4ED8]">{money(earnings.total)}</span></div>
          </div>
        </div>
      )}
    </div>
  );
}
